import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Facebook, Instagram, Youtube, Linkedin, TrendingUp, Users, Calendar, Zap } from 'lucide-react';

const SocialMedia = () => {
  const benefits = [
    "Build credibility fast",
    "Stay top-of-mind",
    "Increase inbound inquiries",
    "Look polished and professional",
    "Save time with streamlined content systems"
  ];

  const platforms = [
    { name: "Facebook", icon: Facebook, description: "Connect with your local community" },
    { name: "Instagram", icon: Instagram, description: "Visual storytelling & engagement" },
    { name: "YouTube", icon: Youtube, description: "Shorts + content planning" },
    { name: "LinkedIn", icon: Linkedin, description: "Professional services (optional)" }
  ];

  const coreDeliverables = [
    "Monthly content strategy",
    "Content calendar planning",
    "AI-assisted copywriting (captions, hooks, CTAs)",
    "Branded post designs (modern + clean)",
    "Short-form video ideas and scripts (reels/shorts)",
    "Hashtag and SEO optimization",
    "Posting schedule support (or done-for-you posting)",
    "Monthly performance summary + recommendations"
  ];

  const targetBusinesses = [
    "Contractors & trades",
    "Property management",
    "Trucking & logistics",
    "Appliance repair & service businesses",
    "Medical/dental clinics",
    "Beauty & wellness providers",
    "Nonprofits and community organizations",
    "Consultants and professional services"
  ];

  const packages = [
    {
      name: "Essentials",
      price: "$500–$900/month",
      description: "Perfect for consistent, professional presence.",
      features: [
        "Monthly strategy + calendar",
        "8–12 posts/month",
        "Captions + hashtags",
        "Branded design style",
        "Monthly reporting summary"
      ]
    },
    {
      name: "Growth",
      price: "$1,000–$1,800/month",
      description: "Designed to drive engagement and inquiries.",
      highlight: true,
      features: [
        "Everything in Essentials plus:",
        "12–20 posts/month",
        "4–8 reels/shorts scripts (or content prompts)",
        "Strong CTAs + lead prompts",
        "Improved visual branding + content themes"
      ]
    },
    {
      name: "Authority",
      price: "$2,000+/month",
      description: "For businesses that want strong visibility, consistent content, and premium positioning.",
      features: [
        "Everything in Growth plus:",
        "Higher content volume",
        "Stronger video strategy",
        "Brand messaging optimization",
        "Monthly campaign planning (promos, launches, offers)",
        "Priority support"
      ]
    }
  ];

  const howItWorks = [
    { step: 1, title: "Strategy Call", description: "Goals + audience + offers" },
    { step: 2, title: "Brand Setup", description: "Style, messaging, content pillars" },
    { step: 3, title: "Content Calendar", description: "Themes + hooks + CTAs" },
    { step: 4, title: "Creation & Posting", description: "Done-with-you or done-for-you" },
    { step: 5, title: "Monthly Reporting", description: "Optimize based on what performs" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F5] via-white to-[#F5F5F5]"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#227AFF]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6">
              <span className="text-gradient">Social Media Management</span>
            </h1>
            <p className="text-2xl font-semibold text-gray-700 mb-8">
              Consistent Content. Stronger Trust. More Customers.
            </p>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Social media isn't just posting — it's positioning. We help small businesses show up professionally, stay visible, and attract the right clients with AI-assisted content systems built for growth.
            </p>
            <Link to="/contact" className="btn btn-primary text-lg px-8">
              Book a Free Strategy Call
            </Link>
          </div>
        </div>
      </section>

      {/* Why Social Media Matters */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Why Social Media Matters for Small Businesses</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              Your customers are already searching, scrolling, and comparing. If your business isn't showing up consistently, you're losing attention — and attention becomes revenue.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold mb-6 text-center">We help you:</h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-[#227AFF] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-center mb-4">What We Manage</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              We support the platforms where your customers spend time:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {platforms.map((platform, index) => {
                const IconComponent = platform.icon;
                return (
                  <div key={index} className="card flex items-start space-x-4">
                    <div className="p-3 rounded-xl bg-[#227AFF]/10">
                      <IconComponent className="h-8 w-8 text-[#227AFF]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg mb-1">{platform.name}</h4>
                      <p className="text-gray-600 text-sm">{platform.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center bg-gradient-to-r from-[#227AFF]/10 to-[#007BFF]/10 rounded-2xl p-6">
              <p className="text-lg font-semibold">
                Not sure what platform you need? We'll recommend the best mix based on your business model.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">What's Included</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              Our social media management is designed to be simple for you and effective for your business.
            </p>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold mb-6">Core Deliverables</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {coreDeliverables.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">Who This Is For</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              This service is perfect for small to medium businesses that want growth without the daily burden of content creation:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {targetBusinesses.map((business, index) => (
                <div key={index} className="flex items-start bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{business}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-center mb-4">Packages</h2>
            <div className="accent-line"></div>
            <p className="text-xl text-center text-gray-600 mb-12 mt-6">
              Choose the package that fits your growth goals
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`card ${pkg.highlight ? 'border-2 border-[#227AFF] relative' : ''}`}
                >
                  {pkg.highlight && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#227AFF] text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-3xl font-bold text-[#227AFF] mb-4">{pkg.price}</p>
                  <p className="text-gray-600 mb-6">{pkg.description}</p>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#227AFF] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`btn w-full ${
                      pkg.highlight ? 'btn-primary' : 'btn-secondary'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center mb-4">How It Works</h2>
            <div className="accent-line"></div>
            
            <div className="grid md:grid-cols-5 gap-6 mt-12">
              {howItWorks.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#227AFF] text-white font-bold text-xl mb-4">
                    {item.step}
                  </div>
                  <h4 className="font-semibold mb-2">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section section-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mb-6 text-white">Ready to grow your business without spending hours on content?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's build a social media system that saves you time and strengthens your brand.
            </p>
            <Link to="/contact" className="btn btn-accent text-lg px-8 animate-glow">
              Book a Free Strategy Call
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialMedia;